/*
 * Automated Discount Handler for Rebuy Smart Cart
 *
 * Handles Shopify-managed automated discount codes (e.g., BLACKFRIDAY10, BLACKFRIDAY25, BLACKFRIDAY40)
 * that are applied at checkout and have line-level discount allocations in the cart data.
 *
 * Key Features:
 * - Reads discount data from line_level_discount_allocations (Shopify source of truth)
 * - Reads cart-level discount applications (URL/UTM discount codes)
 * - Fallback to parsing discount code from DOM (.rebuy-cart__flyout-item-discount-message)
 * - Calculates individual item discounted prices
 * - Updates DOM with correct sale prices and maintains original compare-at prices
 * - Manages footer discount display (tags + amount) for URL discount codes
 * - Handles multiple items with different prices, quantities, and variants
 * - German locale price formatting (€X,XX)
 * - Always uses Shopify cart data as source of truth (no DOM-based price storage)
 * - Recalculates prices on every cart change for accurate updates
 *
 * Architecture:
 * - Subscribes to 'cart:change' event from RebuyEventManager
 * - Processes items with line_level_discount_allocations only
 * - Manages footer discount section for checkout-level (URL/UTM) discounts
 * - Updates DOM without interfering with other handlers (manual discounts, multiples promos)
 * - Compares compare-at-prices directly from Shopify cart item data
 *
 * Last Updated: November 2025
 */

window.AutomatedDiscountHandler = (function () {
  // Private variables
  let debug = true; // Enable debug logging to diagnose URL discount removal
  let currentDiscountCode = null;

  // Track items that should have BlackFriday codes (item ID -> code mapping)
  const blackFridayItemCodes = new Map();

  // Track MutationObservers for discount messages
  const discountMessageObservers = new Map();

  // Discount code to percentage mapping
  const DISCOUNT_CODE_MAP = {
    'BLACKFRIDAY40': 40,
    'BLACKFRIDAY25': 25,
    'BLACKFRIDAY10': 10,
  };

  function log(...args) {
    if (debug) {
      console.log('[AutomatedDiscountHandler]', ...args);
    }
  }

  /**
   * Parse a price string in German locale format (€X,XX) to a number
   * Handles both formatted (€47,99) and plain (47,99) formats
   */
  function parsePriceString(priceStr) {
    if (!priceStr) return null;
    return parseFloat(priceStr.replace('€', '').trim().replace(',', '.'));
  }

  /**
   * Format a number to German locale price string (€X,XX)
   */
  function formatPrice(amount) {
    if (amount === null || amount === undefined) return null;
    const formatted = parseFloat(amount).toFixed(2);
    return '€' + formatted.replace('.', ',');
  }

  /**
   * Get compare-at price from Shopify cart item data
   * This is the authoritative source, not the DOM
   */
  function getCompareAtPriceFromCartItem(cartItem) {
    if (!cartItem.compare_at_price) return null;

    // Shopify stores compare_at_price in cents
    const compareAtPricePerUnit = cartItem.compare_at_price / 100;
    const quantity = cartItem.quantity || 1;

    // Return total compare-at price (per unit × quantity)
    return compareAtPricePerUnit * quantity;
  }

  /**
   * Extract discount code from the item's discount message DOM element
   * Returns the discount code if found (e.g., "BLACKFRIDAY40"), null otherwise
   */
  function extractDiscountCodeFromDOM(itemElement) {
    const discountMessage = itemElement.querySelector('.rebuy-cart__flyout-item-discount-message');
    if (!discountMessage) return null;

    const code = discountMessage.textContent.trim().toUpperCase();
    return code || null;
  }

  /**
   * Add banner notification when BlackFriday discount is applied
   */
  function addBlackFridayBanner(code) {
    // Don't add multiple banners
    if (document.getElementById('blackfriday-banner')) return;

    const cartItemsContainer = document.querySelector('[data-rebuy-component="cart-items"]');
    if (!cartItemsContainer) return;

    const banner = document.createElement('div');
    banner.id = 'blackfriday-banner';
    banner.style.cssText = `
      background-color: #f8f4f4;
      color: #d4335f;
      padding: 8px 15px;
      margin: 0 0 10px 0;
      border-radius: 4px;
      font-weight: bold;
      text-align: center;
      font-size: 14px;
      border-left: 3px solid #d4335f;
      z-index: 1000;
    `;

    banner.innerHTML = `🎉 Offerta del Black Friday applicata! 🎉`;
    cartItemsContainer.parentNode.insertBefore(banner, cartItemsContainer);
    log('BlackFriday banner added for code:', code);
  }

  /**
   * Remove banner notification
   */
  function removeBlackFridayBanner() {
    const banner = document.getElementById('blackfriday-banner');
    if (banner) {
      banner.remove();
      log('BlackFriday banner removed');
    }
  }

  /**
   * Get cart-level discount using centralized RebuyUtils
   * Returns object with code and discount amount or null
   */
  function getCartLevelDiscount() {
    if (!window.RebuyUtils || !window.RebuyUtils.getActiveDiscount) {
      log('RebuyUtils not available');
      return null;
    }

    const discount = window.RebuyUtils.getActiveDiscount();

    // Only return if this is a checkout-level discount (not manual Rebuy or automatic)
    if (discount && discount.source === 'checkout_level') {
      // Calculate amount from cart data
      const cart = window.Rebuy?.Cart?.cart;
      if (cart && cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0) {
        const discountApp = cart.cart_level_discount_applications[0];
        const amount = parseFloat(discountApp.total_allocated_amount) / 100;

        // Try to get code from RebuyUtils first, then fallback to cart discount application data
        let code = discount.code;
        if (!code && discountApp.title) {
          // Extract code from title (e.g., "BLACKFRIDAY25" or "Discount code: BLACKFRIDAY25")
          code = discountApp.title.replace(/^Discount code:\s*/i, '').trim().toUpperCase();
          log('Extracted code from discount application title:', code);
        }

        // Only return if we have a valid code
        if (code) {
          log('Cart-level discount from RebuyUtils:', code, 'Amount:', amount);
          return {
            code: code,
            amount: amount,
            valueType: discount.type,
          };
        } else {
          log('Cart-level discount found but no code available');
        }
      }
    }

    return null;
  }

  /**
   * Remove discount code from cart
   * Clears cookie and clicks Rebuy's native button (even if hidden) to trigger proper removal
   */
  function removeDiscountCode() {
    log('=== REMOVE DISCOUNT CODE CALLED ===');
    log('Current discount code:', currentDiscountCode);

    // Clear discount code cookie (for URL/UTM codes)
    document.cookie = 'discount_code=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    log('✓ Cleared discount_code cookie');

    // Find Rebuy's native remove button
    const discountInputWrapper = document.querySelector('[data-rebuy-component="discount-input"]');
    const nativeRemoveButton = discountInputWrapper?.querySelector('.rebuy-cart__discount-tag-remove');

    log('Discount input wrapper found:', !!discountInputWrapper);
    log('Native remove button found:', !!nativeRemoveButton);

    if (nativeRemoveButton) {
      // Check if wrapper is hidden (URL discount case)
      const wasHidden = discountInputWrapper.style.display === 'none' ||
                        window.getComputedStyle(discountInputWrapper).display === 'none';

      log('Wrapper was hidden:', wasHidden);

      if (wasHidden) {
        // Disconnect URLDiscountManager's observer to prevent re-hiding
        if (discountInputWrapper._observer) {
          discountInputWrapper._observer.disconnect();
          log('Disconnected URLDiscountManager observer');
        }

        log('Temporarily unhiding discount input to click native button');
        // Temporarily unhide to make button clickable
        discountInputWrapper.style.setProperty('display', 'block', 'important');

        // Small delay to ensure DOM updates
        setTimeout(() => {
          // Click the native button (let Rebuy handle removal)
          nativeRemoveButton.click();
          log('Clicked native Rebuy remove button');

          // Also ensure Shopify's cart is updated via Cart Ajax API
          setTimeout(() => {
            if (window.RebuyUtils && window.RebuyUtils.removeDiscountFromCart) {
              window.RebuyUtils.removeDiscountFromCart().then((result) => {
                if (result.success) {
                  log('✓ Ensured discount removed from Shopify checkout via Cart Ajax API');
                }
                showDiscountInput();
              });
            } else {
              showDiscountInput();
            }
          }, 100);
        }, 50);
      } else {
        // Button is visible, just click it
        log('Clicking visible native Rebuy remove button');
        nativeRemoveButton.click();

        // Also ensure Shopify's cart is updated via Cart Ajax API
        setTimeout(() => {
          if (window.RebuyUtils && window.RebuyUtils.removeDiscountFromCart) {
            window.RebuyUtils.removeDiscountFromCart().then((result) => {
              if (result.success) {
                log('✓ Ensured discount removed from Shopify checkout via Cart Ajax API');
              }
              showDiscountInput();
            });
          } else {
            showDiscountInput();
          }
        }, 100);
      }

      // Note: RebuyEventManager will detect this click and trigger 'discount:removed' event after 1000ms
      return;
    }

    // Fallback: Native button not found (URL codes might not populate it)
    log('Native remove button not found, clearing discount via Shopify API');

    // Remove the footer UI immediately
    removeDiscountFooter();

    // Use RebuyUtils to properly remove discount from Shopify cart via Cart Ajax API
    log('Calling RebuyUtils.removeDiscountFromCart() with Cart Ajax API');

    if (window.RebuyUtils && window.RebuyUtils.removeDiscountFromCart) {
      window.RebuyUtils.removeDiscountFromCart().then((result) => {
        if (result.success) {
          log('✓ Discount removed from Shopify cart via Cart Ajax API');

          // Refresh Rebuy cart to sync with Shopify's updated cart
          if (window.RebuyEventManager && window.RebuyEventManager.refreshCart) {
            window.RebuyEventManager.refreshCart().then(() => {
              log('✓ Rebuy cart refreshed and synced with Shopify');

              // Trigger discount:removed event
              if (window.RebuyEventManager) {
                window.RebuyEventManager.triggerEvent('discount:removed', {
                  method: 'cart-ajax-api',
                  cart: result.cart
                });
              }

              // Show the discount input field
              showDiscountInput();
            });
          } else {
            // Fallback: just show the input field
            showDiscountInput();
          }
        } else {
          log('ERROR: Failed to remove discount from Shopify cart:', result.error || 'Unknown error');
          showDiscountInput(); // Show input anyway
        }
      }).catch((error) => {
        console.error('[AutomatedDiscountHandler] Error removing discount:', error);
        showDiscountInput(); // Show input anyway
      });
    } else {
      log('ERROR: RebuyUtils.removeDiscountFromCart not available');
      showDiscountInput(); // Show input anyway
    }
  }

  /**
   * Show the discount input field after discount removal
   */
  function showDiscountInput() {
    const discountInputWrapper = document.querySelector('[data-rebuy-component="discount-input"]');
    if (discountInputWrapper) {
      discountInputWrapper.style.removeProperty('display');
      log('✓ Discount input field shown');
    }
  }

  /**
   * Render discount footer with tags and amount
   * Matches the subtotal_custom alignment (label left, value right)
   */
  function renderDiscountFooter(discountData) {
    if (!discountData) {
      removeDiscountFooter();
      return;
    }

    const discountContainer = document.querySelector('[data-rebuy-component="discount-input"]');
    if (!discountContainer) return;

    // Remove existing footer if present
    removeDiscountFooter();

    // Create discount row matching subtotal_custom structure
    const rowDiv = document.createElement('div');
    rowDiv.id = 'automated-discount-footer-row';
    rowDiv.className = 'subtotal_custom';
    rowDiv.style.cssText = `
      padding-left: 20px;
      padding-right: 20px;
      border-top: none;
      border-bottom: none;
    `;

    // Create left side container for tags and code (takes full width, matches label behavior)
    const leftDiv = document.createElement('div');
    leftDiv.id = 'automated-discount-tags';
    leftDiv.className = 'rebuy-cart__discount-tags';
    leftDiv.style.cssText = `
      width: 100%;
      display: flex;
      align-items: center;
      gap: 8px;
    `;

    const tagDiv = document.createElement('div');
    tagDiv.className = 'rebuy-cart__discount-tag';
    tagDiv.style.cssText = `
      display: flex;
      align-items: center;
      gap: 6px;
      flex-wrap: nowrap;
    `;
    tagDiv.innerHTML = `
      <span class="rebuy-cart__discount-tag-icon" style="display: flex; align-items: center; flex-shrink: 0; color: inherit;">
        <i class="fas fa-tags"></i>
      </span>
      <span class="rebuy-cart__discount-tag-text" style="font-weight: bold; flex-shrink: 0; white-space: nowrap; color: #000; font-size: 13px;">${discountData.code}</span>
      <span class="rebuy-cart__discount-tag-remove" style="cursor: pointer; display: flex; align-items: center; flex-shrink: 0; margin-left: 4px; color: #000;">
        <i class="far fa-times"></i>
      </span>
    `;

    // Attach click handler immediately after setting innerHTML (before DOM insertion)
    const removeButton = tagDiv.querySelector('.rebuy-cart__discount-tag-remove');
    if (removeButton) {
      removeButton.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        log('Remove button clicked for code:', discountData.code);
        removeDiscountCode();
      });
      log('Click handler attached to remove button for code:', discountData.code);
    } else {
      log('ERROR: Could not find remove button element after setting innerHTML');
    }

    leftDiv.appendChild(tagDiv);

    // Log for debugging
    log('Created tag with code:', discountData.code);

    // Create right side for amount (matches price behavior)
    const amountDiv = document.createElement('div');
    amountDiv.id = 'automated-discount-amount';
    amountDiv.className = 'rebuy-cart__discount-amount';
    amountDiv.style.cssText = `
      font-weight: bold;
      color: #d4335f;
      white-space: nowrap;
      flex-shrink: 0;
    `;
    amountDiv.innerHTML = `- ${formatPrice(discountData.amount)}`;

    // Assemble row
    rowDiv.appendChild(leftDiv);
    rowDiv.appendChild(amountDiv);

    // Insert after discount container
    discountContainer.parentNode.insertBefore(rowDiv, discountContainer.nextSibling);

    // Remove borders from URLDiscountManager's notice if it exists
    const notice = document.getElementById('url-discount-notice');
    if (notice) {
      notice.style.borderTop = 'none';
      notice.style.borderBottom = 'none';
      notice.style.paddingTop = '0px';
      notice.style.paddingBottom = '0px';
      notice.style.paddingLeft = '20px';
      notice.style.paddingRight = '20px';
    }

    log('Discount footer rendered matching subtotal layout:', discountData);
  }

  /**
   * Remove discount footer elements
   */
  function removeDiscountFooter() {
    const row = document.getElementById('automated-discount-footer-row');

    if (row && row.parentNode) {
      row.remove();
      log('Discount footer row removed');
      return;
    }

    // Fallback for old structure (wrapper div)
    const wrapper = document.getElementById('automated-discount-footer-wrapper');
    if (wrapper && wrapper.parentNode) {
      wrapper.remove();
      log('Discount footer wrapper removed');
      return;
    }

    log('No discount footer found to remove');
  }

  /**
   * Get discount percentage for a given code
   * Uses fallback to parse percentage from code name if not in map
   */
  function getDiscountPercentageForCode(code) {
    if (!code) return 0;

    // Check if code is in mapping
    if (DISCOUNT_CODE_MAP[code]) {
      return DISCOUNT_CODE_MAP[code];
    }

    // Fallback: Try to parse percentage from code (e.g., "BLACKFRIDAY40" -> 40)
    const match = code.match(/(\d+)$/);
    if (match) {
      const percentage = parseInt(match[1], 10);
      if (percentage > 0 && percentage <= 100) {
        return percentage;
      }
    }

    return 0;
  }

  /**
   * Get BlackFriday code from line-level discount allocation
   * Extracts the discount percentage and maps it to the corresponding code
   */
  function getBlackFridayCodeFromAllocation(cartItem) {
    if (!hasLineItemDiscount(cartItem)) return null;

    const allocation = cartItem.line_level_discount_allocations[0];
    if (!allocation || !allocation.discount_application) return null;

    const discountApp = allocation.discount_application;

    // Check if this is a percentage discount
    if (discountApp.value_type !== 'percentage') return null;

    const percentage = parseFloat(discountApp.value);

    // Map percentage to BlackFriday code
    if (percentage === 40) return 'BLACKFRIDAY40';
    if (percentage === 25) return 'BLACKFRIDAY25';
    if (percentage === 10) return 'BLACKFRIDAY10';

    log('Line-level discount detected but not a BlackFriday code:', percentage);
    return null;
  }

  /**
   * Setup MutationObserver to enforce BlackFriday code priority
   * Prevents Rebuy from overwriting BlackFriday codes with other discount codes
   */
  function enforceBlackFridayCodePriority(itemElement, itemId, blackFridayCode) {
    const discountMessage = itemElement.querySelector('.rebuy-cart__flyout-item-discount-message');
    if (!discountMessage) {
      log('Discount message element not found for observer setup');
      return;
    }

    // Disconnect existing observer for this item if present
    if (discountMessageObservers.has(itemId)) {
      discountMessageObservers.get(itemId).disconnect();
      log('Disconnected existing observer for item', itemId);
    }

    // Create MutationObserver to watch for text changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'characterData' || mutation.type === 'childList') {
          const currentText = discountMessage.textContent.trim().toUpperCase();

          // If someone tries to change it to something other than our BlackFriday code, change it back
          if (currentText !== blackFridayCode) {
            log('⚠️ ENFORCING BlackFriday code priority! Overriding', currentText, 'back to', blackFridayCode);
            discountMessage.textContent = blackFridayCode;
          }
        }
      });
    });

    // Start observing
    observer.observe(discountMessage, {
      characterData: true,
      childList: true,
      subtree: true
    });

    // Store observer for cleanup
    discountMessageObservers.set(itemId, observer);
    log('✓ Enforcing BlackFriday code priority for item', itemId, 'with code:', blackFridayCode);
  }

  /**
   * Clear BlackFriday code tracking for items no longer in cart or without discounts
   */
  function clearBlackFridayCodeTracking(itemId) {
    // Disconnect observer if present
    if (discountMessageObservers.has(itemId)) {
      discountMessageObservers.get(itemId).disconnect();
      discountMessageObservers.delete(itemId);
      log('Cleared BlackFriday observer for item', itemId);
    }

    // Remove from tracking map
    if (blackFridayItemCodes.has(itemId)) {
      blackFridayItemCodes.delete(itemId);
      log('Cleared BlackFriday code tracking for item', itemId);
    }
  }

  /**
   * Update the discount message DOM element to show the correct code
   * Overrides Rebuy's default message when BlackFriday codes should take priority
   * Sets up MutationObserver to prevent future overwrites
   */
  function updateDiscountMessageDOM(itemElement, itemId, code) {
    const discountMessage = itemElement.querySelector('.rebuy-cart__flyout-item-discount-message');
    if (!discountMessage) {
      log('Discount message element not found for item');
      return;
    }

    const currentMessage = discountMessage.textContent.trim().toUpperCase();
    if (currentMessage === code) {
      log('Discount message already correct:', code);
      // Still setup observer to prevent future changes
      if (DISCOUNT_CODE_MAP[code]) {
        blackFridayItemCodes.set(itemId, code);
        enforceBlackFridayCodePriority(itemElement, itemId, code);
      }
      return;
    }

    log('Overriding discount message from', currentMessage, 'to', code);
    discountMessage.textContent = code;

    // Track this BlackFriday code and setup observer to prevent overwrites
    if (DISCOUNT_CODE_MAP[code]) {
      blackFridayItemCodes.set(itemId, code);
      enforceBlackFridayCodePriority(itemElement, itemId, code);
    }
  }

  /**
   * Check if an item has line-level discount allocations (Shopify automated discount)
   */
  function hasLineItemDiscount(cartItem) {
    return (
      cartItem &&
      cartItem.line_level_discount_allocations &&
      Array.isArray(cartItem.line_level_discount_allocations) &&
      cartItem.line_level_discount_allocations.length > 0
    );
  }

  /**
   * Get discount amount for an item from line-level allocations
   * Returns discount amount in euros (calculated from Shopify data in cents)
   */
  function getDiscountAmountFromLineAllocation(cartItem) {
    if (!hasLineItemDiscount(cartItem)) return 0;

    const allocation = cartItem.line_level_discount_allocations[0];
    if (!allocation || !allocation.amount) return 0;

    // Shopify stores amount in cents
    return allocation.amount / 100;
  }

  /**
   * Calculate discounted price for an item
   * Accounts for quantity - discount is applied to total line price (all units)
   * Priority: Use line-level allocation data, fallback to code-based calculation
   * Returns object with both discounted and original prices
   */
  function calculateDiscountedPrice(cartItem, itemElement) {
    const itemId = cartItem.id;
    const pricePerUnit = cartItem.price / 100; // Shopify stores in cents
    const quantity = cartItem.quantity || 1;
    const totalOriginalPrice = pricePerUnit * quantity;

    // Primary: Use line-level discount allocations from Shopify
    const discountAmount = getDiscountAmountFromLineAllocation(cartItem);
    if (discountAmount > 0) {
      const totalDiscountedPrice = totalOriginalPrice - discountAmount;
      log('Calculated price from line-level allocation:', {
        itemId,
        quantity,
        pricePerUnit,
        totalOriginalPrice,
        discountAmount,
        totalDiscountedPrice,
      });
      return {
        discountedPrice: Math.max(0, totalDiscountedPrice),
        originalPrice: totalOriginalPrice,
      };
    }

    // Fallback: Try to parse discount code from DOM and calculate
    const code = extractDiscountCodeFromDOM(itemElement);
    const percentage = getDiscountPercentageForCode(code);

    if (percentage > 0) {
      const totalDiscountedPrice = totalOriginalPrice * (1 - percentage / 100);
      log('Calculated price from code-based discount:', {
        itemId,
        quantity,
        code,
        percentage,
        totalOriginalPrice,
        totalDiscountedPrice,
      });
      return {
        discountedPrice: Math.round(totalDiscountedPrice * 100) / 100,
        originalPrice: totalOriginalPrice,
      };
    }

    return null; // No discount found
  }

  /**
   * Update the DOM for an item's price
   * Always shows original price as strikethrough and discounted price as sale price
   */
  function updateItemPriceDOM(cartItem, itemElement, discountedPrice, originalPrice) {
    const priceContainer = itemElement.querySelector('.rebuy-cart__flyout-item-price');
    if (!priceContainer) return;

    // Always show the original price as strikethrough with discounted price
    const priceHTML = `
      <div>
        <span class="rebuy-money sale">
          <span class="sr-only">Sale Price</span>
          <span>${formatPrice(discountedPrice)}</span>
        </span>
        <span class="rebuy-money compare-at">
          <span class="sr-only">Original Price</span>
          <span>${formatPrice(originalPrice)}</span>
        </span>
      </div>
    `;

    priceContainer.innerHTML = priceHTML;

    log('Updated price DOM for item', cartItem.id, {
      discountedPrice,
      originalPrice,
    });
  }

  /**
   * Process all cart items and update prices for those with line-level discounts
   * Also manages footer discount display for URL/UTM codes
   */
  function processCartItems(cart) {
    if (!cart || !cart.items || cart.items.length === 0) {
      log('No cart items to process');
      removeBlackFridayBanner();
      removeDiscountFooter();
      currentDiscountCode = null;

      // Clear all BlackFriday tracking when cart is empty
      blackFridayItemCodes.forEach((_code, itemId) => {
        clearBlackFridayCodeTracking(itemId);
      });

      return;
    }

    // Clean up observers for items no longer in cart
    const currentItemIds = new Set(cart.items.map(item => item.id));
    blackFridayItemCodes.forEach((_code, itemId) => {
      if (!currentItemIds.has(itemId)) {
        clearBlackFridayCodeTracking(itemId);
      }
    });

    // Check for cart-level discount (URL/UTM code) using centralized RebuyUtils
    const cartLevelDiscount = getCartLevelDiscount();
    log('Cart-level discount detected:', cartLevelDiscount);

    // Get all item elements from DOM
    const itemElements = document.querySelectorAll('[data-item-id]');
    let hasBlackFridayDiscount = false;
    let detectedCode = null;

    cart.items.forEach(cartItem => {
      // Find the corresponding DOM element for this item
      const itemElement = Array.from(itemElements).find(
        el => el.getAttribute('data-item-id') === String(cartItem.id)
      );

      if (!itemElement) {
        log('DOM element not found for item', cartItem.id);
        return;
      }

      // Check if item has a line-level discount
      if (!hasLineItemDiscount(cartItem)) {
        log('No line-level discount for item', cartItem.id);
        return;
      }

      // Get BlackFriday code from Shopify's allocation data (source of truth)
      const blackFridayCode = getBlackFridayCodeFromAllocation(cartItem);

      if (blackFridayCode) {
        // Override the discount message to show BlackFriday code instead of cart-level code
        // Setup MutationObserver to prevent any future overwrites
        updateDiscountMessageDOM(itemElement, cartItem.id, blackFridayCode);
        hasBlackFridayDiscount = true;
        detectedCode = blackFridayCode;
        log('BlackFriday code detected from allocation:', blackFridayCode);
      } else {
        // Fallback: Extract discount code from DOM (for non-BlackFriday codes)
        const code = extractDiscountCodeFromDOM(itemElement);
        if (code && DISCOUNT_CODE_MAP[code]) {
          // This is a BlackFriday code in the DOM but not in allocation data
          // Still enforce priority with observer
          updateDiscountMessageDOM(itemElement, cartItem.id, code);
          hasBlackFridayDiscount = true;
          detectedCode = code;
        } else {
          // Not a BlackFriday code, clear any tracking
          clearBlackFridayCodeTracking(cartItem.id);
        }
      }

      // Calculate discounted price (returns object with both prices)
      const priceData = calculateDiscountedPrice(cartItem, itemElement);
      if (priceData === null) {
        log('Could not calculate discounted price for item', cartItem.id);
        return;
      }

      // Update DOM with both discounted and original prices
      updateItemPriceDOM(cartItem, itemElement, priceData.discountedPrice, priceData.originalPrice);
    });

    // Handle BlackFriday banner display
    if (hasBlackFridayDiscount && detectedCode) {
      if (currentDiscountCode !== detectedCode) {
        currentDiscountCode = detectedCode;
        addBlackFridayBanner(detectedCode);
      }
    } else {
      removeBlackFridayBanner();
      currentDiscountCode = null;
    }

    // Handle footer discount display (for URL/UTM codes)
    // Only render footer if URLDiscountManager is not hiding the input
    // (i.e., when there's a cart-level discount that URLDiscountManager already detected)
    if (cartLevelDiscount) {
      log('Rendering footer for cart-level discount:', cartLevelDiscount.code);
      // Add a small delay to let URLDiscountManager finish hiding the input
      setTimeout(() => {
        renderDiscountFooter(cartLevelDiscount);
      }, 100);
    } else {
      removeDiscountFooter();
    }
  }

  /**
   * Handle cart changes
   */
  function onCartChange(eventData) {
    const cart = eventData.cart;
    log('Cart change event received', { itemCount: cart?.items?.length });

    processCartItems(cart);
  }

  /**
   * Handle discount removed event
   */
  function onDiscountRemoved(eventData) {
    log('discount:removed event received');
    // Remove the footer display since discount is gone
    removeDiscountFooter();
    // Show the discount input field
    showDiscountInput();
    // Process cart items to ensure prices are correct
    if (eventData && eventData.cart) {
      processCartItems(eventData.cart);
    }
  }

  /**
   * Initialize the handler
   */
  function init() {
    // Subscribe to cart change events
    if (window.RebuyEventManager) {
      window.RebuyEventManager.on('cart:change', onCartChange)
        .on('cart:ready', onCartChange)
        .on('cart:show', onCartChange)
        .on('discount:removed', onDiscountRemoved);
      log('Initialized - subscribed to cart:change, cart:ready, cart:show, and discount:removed events');
    } else {
      console.error('[AutomatedDiscountHandler] RebuyEventManager not found');
    }
  }

  // Public API
  return {
    init: init,
    enableDebug: function () {
      debug = true;
      return this;
    },
    disableDebug: function () {
      debug = false;
      return this;
    },
  };
})();

// Auto-initialize when document is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function () {
    window.AutomatedDiscountHandler.init();
  });
} else {
  window.AutomatedDiscountHandler.init();
}
